import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import ScenarioCalendar, { SCENARIO_COLORS } from "../components/planner/ScenarioCalendar";
import WeekView from "../components/planner/WeekView";
import ScenarioCard from "../components/planner/ScenarioCard";

function monthShift(month, delta) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function dayShift(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function sundayOf(iso) {
  return dayShift(iso, -new Date(iso + "T00:00:00").getDay());
}

function fmtShort(iso) {
  const d = new Date(iso + "T00:00:00");
  return `${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
}
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function Planner() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [view, setView] = useState("month");
  const [weekStart, setWeekStart] = useState(() => sundayOf(new Date().toISOString().slice(0, 10)));
  const [calendar, setCalendar] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [hidden, setHidden] = useState(() => new Set());
  const [hoveredId, setHoveredId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    try {
      const calUrl = view === "week"
        ? `/scenarios/calendar?start=${weekStart}&end=${dayShift(weekStart, 6)}`
        : `/scenarios/calendar?month=${month}`;
      const [cal, sc] = await Promise.all([
        api.get(calUrl),
        api.get("/scenarios"),
      ]);
      setCalendar(cal.data);
      setScenarios(sc.data);
    } catch {
      toast.error("Could not load planner data");
    }
  }, [month, view, weekStart]);

  useEffect(() => { load(); }, [load]);

  const drafts = useMemo(() => (scenarios || []).filter((s) => s.status === "draft"), [scenarios]);
  const committed = useMemo(() => (scenarios || []).filter((s) => s.status === "committed"), [scenarios]);

  const createScenario = async () => {
    const name = newName.trim() || `Scenario ${String.fromCharCode(65 + drafts.length)}`;
    try {
      await api.post("/scenarios", { name, items: [] });
      setNewName("");
      setCreating(false);
      load();
    } catch {
      toast.error("Could not create scenario");
    }
  };

  const toggleVisible = (id) => {
    setHidden((h) => {
      const n = new Set(h);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const [y, m] = month.split("-").map(Number);

  return (
    <div className="min-h-screen bg-surface">
      <main className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-10" data-testid="planner-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="label-tag mb-2">Decision engine</p>
            <h1 className="font-display font-medium tracking-tight text-4xl sm:text-5xl">Scenario Planner</h1>
            <p className="text-sand-600 mt-2 max-w-xl text-sm leading-relaxed">
              See where you'll be, sketch competing trip combinations, and compare live cost &amp; travel time before committing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/plan/ai" className="btn-swiss btn-ghost text-sm" data-testid="planner-ask-ai">
              <Sparkles size={14} className="mr-1.5" /> Ask AI to draft one
            </Link>
            <button onClick={() => setCreating(true)} className="btn-swiss btn-primary text-sm" data-testid="planner-new-scenario">
              <Plus size={14} className="mr-1.5" /> New scenario
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => { setCalendar(null); view === "week" ? setWeekStart((w) => dayShift(w, -7)) : setMonth((mm) => monthShift(mm, -1)); }}
              className="p-2 border border-sand-300 hover:border-ink transition-colors" data-testid="calendar-prev-month">
              <ChevronLeft size={14} />
            </button>
            <h2 className="font-display font-medium tracking-tight text-lg sm:text-xl w-auto sm:w-56" data-testid="calendar-month-label">
              {view === "week"
                ? `${fmtShort(weekStart)} – ${fmtShort(dayShift(weekStart, 6))}, ${weekStart.slice(0, 4)}`
                : `${MONTH_NAMES[m - 1]} ${y}`}
            </h2>
            <button
              onClick={() => { setCalendar(null); view === "week" ? setWeekStart((w) => dayShift(w, 7)) : setMonth((mm) => monthShift(mm, 1)); }}
              className="p-2 border border-sand-300 hover:border-ink transition-colors" data-testid="calendar-next-month">
              <ChevronRight size={14} />
            </button>
            <div className="flex border border-sand-300 ml-1" data-testid="calendar-view-toggle">
              <button
                onClick={() => { setCalendar(null); setMonth(weekStart.slice(0, 7)); setView("month"); }}
                className={`font-mono text-[10px] uppercase tracking-wide px-3 py-1.5 transition-colors ${view === "month" ? "bg-black text-white" : "text-sand-600 hover:bg-sand-100"}`}
                data-testid="calendar-view-month">
                Month
              </button>
              <button
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10);
                  setCalendar(null);
                  setWeekStart(sundayOf(today.slice(0, 7) === month ? today : `${month}-01`));
                  setView("week");
                }}
                className={`font-mono text-[10px] uppercase tracking-wide px-3 py-1.5 transition-colors ${view === "week" ? "bg-black text-white" : "text-sand-600 hover:bg-sand-100"}`}
                data-testid="calendar-view-week">
                Week
              </button>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-4 font-mono text-[10px] uppercase tracking-wide text-sand-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 bg-ember inline-block" /> Confirmed</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 border border-ember/50 planned-stripes inline-block" /> Planned</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 border border-dashed border-sand-500 inline-block" /> Scenario</span>
            <span>SJU = projected location · * = from planned{view === "month" ? " · click a day to zoom" : ""}</span>
          </div>
        </div>

        {view === "week" ? (
          <WeekView data={calendar} scenarios={drafts} hiddenIds={hidden} hoveredId={hoveredId} />
        ) : (
          <ScenarioCalendar data={calendar} month={month} scenarios={drafts} hiddenIds={hidden} hoveredId={hoveredId}
            onDayClick={(iso) => { setCalendar(null); setWeekStart(sundayOf(iso)); setView("week"); }} />
        )}

        <div className="mt-12">
          <div className="flex items-center justify-between border-b-2 border-ink pb-2 mb-6">
            <h2 className="font-display font-medium tracking-tight text-2xl">Compare scenarios</h2>
            <span className="label-tag">{drafts.length} draft{drafts.length === 1 ? "" : "s"}</span>
          </div>

          {scenarios === null ? (
            <div className="border border-sand-200 p-12 text-center label-tag animate-pulse">Loading…</div>
          ) : drafts.length === 0 && committed.length === 0 ? (
            <div className="border-2 border-dashed border-sand-300 p-16 text-center" data-testid="planner-empty-state">
              <p className="font-display font-medium text-2xl text-sand-300 mb-3">No scenarios yet</p>
              <p className="text-sm text-sand-500 max-w-md mx-auto mb-6">
                Weighing "Denver concert then Durango" against "Durango then Seattle"? Build each as a scenario and let the numbers decide.
              </p>
              <button onClick={() => setCreating(true)} className="btn-swiss btn-primary" data-testid="planner-empty-new">
                <Plus size={14} className="mr-1.5" /> Build your first scenario
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {drafts.map((sc, i) => (
                <ScenarioCard key={sc.id} scenario={sc} color={SCENARIO_COLORS[i % SCENARIO_COLORS.length].hex}
                  onChanged={load} onHover={setHoveredId} hidden={hidden.has(sc.id)} onToggleVisible={toggleVisible} />
              ))}
            </div>
          )}

          {committed.length > 0 && (
            <div className="mt-10">
              <p className="label-tag mb-4">Committed</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {committed.map((sc) => (
                  <ScenarioCard key={sc.id} scenario={sc} color="#8A7E74" onChanged={load} onHover={() => {}} hidden onToggleVisible={() => {}} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-surface border-2 border-ink w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="new-scenario-dialog">
            <p className="label-tag mb-3">New scenario</p>
            <input autoFocus className="swiss-input" placeholder={`e.g. Denver 30th → Durango`}
              value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createScenario()}
              data-testid="new-scenario-name" />
            <div className="flex gap-2 mt-4">
              <button onClick={createScenario} className="btn-swiss btn-primary flex-1" data-testid="new-scenario-confirm">Create</button>
              <button onClick={() => setCreating(false)} className="btn-swiss btn-ghost" data-testid="new-scenario-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
